import { useCallback, useEffect, useMemo } from 'react';

import type { AssistanceServiceInputParam } from '@atlassian/conversation-assistant-service/services/assistance-service/main';

import { type ConversationAction, PageContextType } from '../../common/types';
import { useActionsContext } from '../../common/utils/actions-context';
import { createPlaceholderAction } from '../../common/utils/create-placeholder-action';
import {
	useConversationActionsStoreActions,
	useConversationActionsStoreState,
} from '../conversation-actions-store';
import { useRovoConversationStarters } from '../rovo-conversation-starters';

export type UseActionsFetchingProps = {
	pageContextType: PageContextType;
	location: string;
	assistanceServiceParams?: AssistanceServiceInputParam['assistanceServiceParams'];
	pageContextSupportedForDynamicStarters: boolean;
};

export type UseActionsFetchingResult = {
	actions: ConversationAction[];
	isDynamicStarterActionsLoading: boolean;
};

export const useActionsFetching = ({
	pageContextType,
	location,
	assistanceServiceParams,
	pageContextSupportedForDynamicStarters,
}: UseActionsFetchingProps): UseActionsFetchingResult => {
	const { setConversationActions } = useConversationActionsStoreActions();
	const {
		actions: actionsFromStore,
		jiraIssueViewActions: jiraIssueViewActionsFromStore,
		convoStarterActionPrompt,
	} = useConversationActionsStoreState();

	const {
		getActions,
		prioritizeCustomActionResults = false,
		numStartersToShow = 3,
	} = useActionsContext();

	const {
		conversationStarterActions,
		isDynamicStarterActionsLoading,
		numDynamicStarterActionsLoading,
	} = useRovoConversationStarters({
		dynamicStartersConfig: {
			enabled: pageContextSupportedForDynamicStarters,
		},
		assistanceServiceParams,
	});

	const placeholderActions = useMemo(() => {
		return Array(numDynamicStarterActionsLoading)
			.fill(null)
			.map((_, i) => createPlaceholderAction(i));
	}, [numDynamicStarterActionsLoading]);

	const fetchActionsFromProvider = useCallback(async () => {
		if (!getActions) {
			return [];
		}

		const aiActions = (await getActions()) ?? [];
		return aiActions;
	}, [getActions]);

	const getActionsToStoreInState = useCallback(
		async ({ isJiraIssueView }: { isJiraIssueView: boolean }) => {
			/*
			 * Jira Issue View does not use the provider pattern yet, so we need to skip the provider action fetching.
			 * When Jira Issue View is migrated to set actions using provider pattern, we can remove this logic.
			 *
			 * However, when prioritizeCustomActionResults is true, a caller has explicitly requested to replace
			 * all actions (e.g. via setEmptyChatLaunch with replaceProviderActions=true). In that case we must
			 * call fetchActionsFromProvider() so their custom actions flow through correctly, even on Jira Issue View.
			 */
			const aiActionsFromProvider =
				isJiraIssueView && !prioritizeCustomActionResults
					? (jiraIssueViewActionsFromStore ?? [])
					: await fetchActionsFromProvider();

			// Show all 4 starters for Bitbucket pull request page, 3 for all other pages
			const baseNumConvoStartersDisplayed =
				pageContextType === PageContextType.BITBUCKET_PULL_REQUEST ? 4 : numStartersToShow;

			// Find the matching starter from the actions panel list by prompt
			const matchedNudgeStarter = convoStarterActionPrompt
				? conversationStarterActions.find(
						(s) => 'staticPrompt' in s && s.staticPrompt === convoStarterActionPrompt,
					)
				: undefined;

			// When the nudge convo starter is pinned at position 0, reduce the dynamic starter
			// count by 1 so the total visible actions stays at the expected limit.
			const numConvoStartersDisplayed = matchedNudgeStarter
				? Math.max(0, baseNumConvoStartersDisplayed - 1)
				: baseNumConvoStartersDisplayed;

			const updatedActions: ConversationAction[] =
				pageContextSupportedForDynamicStarters && isDynamicStarterActionsLoading
					? [...placeholderActions]
					: conversationStarterActions
							.filter((s) => !('staticPrompt' in s) || s.staticPrompt !== convoStarterActionPrompt)
							.slice(0, numConvoStartersDisplayed);

			updatedActions.unshift(...aiActionsFromProvider);

			if (prioritizeCustomActionResults) {
				/**
				 * Display ONLY the actions fetched from the provider when prioritizeCustomActionResults is true.
				 * This is to allow for custom implementations to fully control the actions displayed.
				 *
				 * HOWEVER, we still backfill with static starters if fewer than numConvoStartersDisplayed actions are provided by the custom implementation.
				 */
				const customActionsWithBackups = aiActionsFromProvider
					.concat(conversationStarterActions)
					.slice(0, baseNumConvoStartersDisplayed);
				return customActionsWithBackups;
			}

			// Pin the matched nudge starter at position 0.
			if (matchedNudgeStarter) {
				updatedActions.unshift(matchedNudgeStarter);
			}

			return updatedActions;
		},
		[
			isDynamicStarterActionsLoading,
			conversationStarterActions,
			fetchActionsFromProvider,
			placeholderActions,
			pageContextSupportedForDynamicStarters,
			jiraIssueViewActionsFromStore,
			prioritizeCustomActionResults,
			pageContextType,
			numStartersToShow,
			convoStarterActionPrompt,
		],
	);

	useEffect(() => {
		const initializeActions = async ({ isJiraIssueView }: { isJiraIssueView: boolean }) => {
			const baseActions = await getActionsToStoreInState({ isJiraIssueView });
			setConversationActions(baseActions);
		};

		initializeActions({ isJiraIssueView: pageContextType === PageContextType.JIRA_ISSUE_VIEW });
	}, [
		pageContextType,
		location,
		getActions,
		getActionsToStoreInState,
		setConversationActions,
		jiraIssueViewActionsFromStore,
	]);

	return useMemo(
		() => ({
			actions: actionsFromStore,
			isDynamicStarterActionsLoading,
		}),
		[actionsFromStore, isDynamicStarterActionsLoading],
	);
};
